// «Tutti i condomìni» e «Stessa offerta per tutti» (mockup totale-condomini, approvato il 24/10): il riquadro in cima
// alla pagina dal computer, la barra fissa in basso e il suo pannello sul telefono e sul Fold.
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Send } from "lucide-react";
import type { OffertaMenu, Vista } from "@/api/preventivo";
import { PannelloBasso } from "@/components/ui/PannelloBasso";
import { anno, totaleCondomini, type Totale } from "@/lib/preventivoFormato";
import { totaleInCima } from "@/lib/linkPreventivo";
import { FOCUS, Importo } from "./parti";
import { SceltaOfferta } from "./SceltaOfferta";
import { FATTO, STESSA_OFFERTA, STESSA_OFFERTA_BREVE, TUTTI_CONDOMINI, condominiForniture, fornitureDiverse, suCondomini } from "./scritte";
import { INVIA, INVIA_AMMINISTRATORE, statoInvio } from "./scritteLista";

interface Props {
  vista: Vista;
  /** Una scelta è in volo: menù spenti. */
  occupato: boolean;
  onScegli: (offertaId: string) => void;
  /** Avviso di una scelta non salvata, sotto i menù. */
  avviso?: ReactNode;
}

function offertaComune(vista: Vista, t: Totale): OffertaMenu | null {
  return t.offerta === null ? null : vista.offerte.find((o) => o.id === t.offerta) ?? null;
}

/** I menù (gli stessi dell'offerta del condominio) e, se qualche fornitura ha un'offerta sua, che resta com'è. */
function Menu({ vista, t, occupato, onScegli, avviso }: Props & { t: Totale }) {
  const offerta = offertaComune(vista, t);
  return (
    <>
      <SceltaOfferta offerte={vista.offerte} valore={offerta} onScegli={onScegli} contesto="condominio" disabilitato={occupato} avviso={avviso} />
      {offerta && t.diverse > 0 && <p className="mt-2 text-xs text-muted-foreground">{fornitureDiverse(t.diverse)}</p>}
    </>
  );
}

/** «−2.334 € l'anno, a consumi uguali» e «su 5 dei 7 condomìni», sotto la cifra. */
function Note({ t }: { t: Totale }) {
  return (
    <>
      {t.anno !== null && <p className="mt-1 text-sm text-muted-foreground">{anno(t.anno)}</p>}
      {t.contati < t.condomini && <p className="text-sm text-muted-foreground">{suCondomini(t.contati, t.condomini)}</p>}
    </>
  );
}

/** «TUTTI I CONDOMÌNI», «7 condomìni · 16 forniture», la cifra al mese e le note: la parte sinistra del riquadro,
 *  la stessa nella pagina dell'operatore e nella copia per l'amministratore. */
function Totale({ t }: { t: Totale }) {
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{TUTTI_CONDOMINI}</p>
      <p className="mt-0.5 text-sm text-muted-foreground">{condominiForniture(t.condomini, t.forniture)}</p>
      {t.mese !== null && (
        <>
          <p className="mt-2 whitespace-nowrap font-heading text-3xl font-semibold leading-none">
            <Importo x={t.mese} />
            <span className="font-sans text-sm font-medium text-text"> al mese</span>
          </p>
          <Note t={t} />
        </>
      )}
    </div>
  );
}

/** «Invia all'amministratore» dal computer, con lo stato dell'invio sotto («Ancora non inviato», «Ultimo invio: mail
 *  del 29/10»): apre il pannello d'invio (29/10). Anche da solo, quando non c'è «Stessa offerta per tutti» (nessuna
 *  offerta in vendita) ma c'è qualcosa da mandare. */
export function TastoInvia({ onInvia, invii, className = "" }: { onInvia: () => void; invii: Vista["invii"]; className?: string }) {
  return (
    <div className={className}>
      <button
        type="button"
        // focus: il pannello chiudendosi lo rimette su chi l'ha aperto (Safari non lo dà al tocco)
        onClick={(e) => { e.currentTarget.focus(); onInvia(); }}
        className={`inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-brand-dark px-5 text-sm font-semibold text-white whitespace-nowrap cursor-pointer hover:bg-dark transition-colors ${FOCUS} focus-visible:ring-offset-2`}
      >
        <Send size={16} aria-hidden="true" />
        {INVIA_AMMINISTRATORE}
      </button>
      <p className="mt-1.5 text-xs text-muted-foreground">{statoInvio(invii ?? [])}</p>
    </div>
  );
}

/** Dal computer, in cima alla pagina sopra la tabella (variante A): il totale a sinistra, i menù a destra e, se l'invio
 *  è acceso, «Invia all'amministratore» in una colonna sua staccata da una riga (variante D3, Alfonso 30/10): sotto il
 *  tasto i menù sembravano il «conferma» dell'offerta. Dai 1280 px tre colonne; fra 1024 e 1279 non ci stanno e il tasto
 *  scende sotto, staccato da una riga orizzontale. */
export function RiquadroTutti({ className = "", onInvia, ...props }: Props & { className?: string; onInvia?: () => void }) {
  const t = totaleCondomini(props.vista.condomini);
  return (
    <section aria-label={TUTTI_CONDOMINI} className={`rounded-xl border border-text/40 bg-card p-4 xl:p-5 ${className}`}>
      <div className={`grid grid-cols-[minmax(0,1fr)_360px] gap-x-6 ${onInvia ? "xl:grid-cols-[minmax(0,1fr)_360px_auto]" : ""}`}>
        <Totale t={t} />
        <div>
          <p className="mb-2 text-sm font-semibold text-text">{STESSA_OFFERTA}</p>
          <Menu {...props} t={t} />
        </div>
        {onInvia && (
          <TastoInvia
            onInvia={onInvia}
            invii={props.vista.invii}
            className="col-span-2 mt-4 flex flex-col items-end border-t border-border pt-4 xl:col-span-1 xl:mt-0 xl:items-start xl:border-t-0 xl:border-l xl:pt-7 xl:pl-6"
          />
        )}
      </div>
    </section>
  );
}

/** Sul telefono e sul Fold (variante B): sempre in basso, il totale al mese e il pulsante che apre il pannello. Sopra la
 *  barra del telefono (zona sicura), sotto il pannello dal basso e l'invito a installare. Montata in `body` come il
 *  pannello: dentro la pagina un contenitore del layout la staccava di 20 px dal fondo dello schermo. Con l'invio acceso
 *  c'è anche «Invia» (29/10), così si manda da qualunque punto della pagina; accanto, «Stessa offerta per tutti» si
 *  accorcia in «Stessa offerta». Senza offerte in vendita resta solo «Invia». */
export function BarraTutti({ vista, onApri, onInvia, className = "" }: { vista: Vista; onApri?: () => void; onInvia?: () => void; className?: string }) {
  const t = totaleCondomini(vista.condomini);
  return createPortal(
    <div
      role="region"
      aria-label={TUTTI_CONDOMINI}
      className={`fixed inset-x-0 bottom-0 z-30 border-t border-text/20 bg-card px-4 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-[0_-4px_12px_rgba(0,0,0,0.06)] md:left-[220px] lg:hidden ${className}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-muted-foreground">{TUTTI_CONDOMINI}</p>
          {t.mese !== null && (
            // la cifra non si spezza; «al mese» va a capo se i due tasti lasciano poco posto (344 px, cifre a quattro numeri)
            <p className="font-semibold">
              <span className="whitespace-nowrap"><Importo x={t.mese} /></span> <span className="whitespace-nowrap text-xs font-medium text-text">al mese</span>
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {onApri && (
            <button
              type="button"
              // focus: il pannello chiudendosi lo rimette su chi l'ha aperto (Safari non lo dà al tocco)
              onClick={(e) => { e.currentTarget.focus(); onApri(); }}
              className={`h-11 shrink-0 rounded-lg border border-border bg-card px-3 text-sm font-medium text-text whitespace-nowrap cursor-pointer hover:bg-surface transition-colors ${FOCUS}`}
            >
              {onInvia ? STESSA_OFFERTA_BREVE : STESSA_OFFERTA}
            </button>
          )}
          {onInvia && (
            <button
              type="button"
              onClick={(e) => { e.currentTarget.focus(); onInvia(); }}
              className={`inline-flex h-11 shrink-0 items-center gap-1.5 rounded-lg bg-brand-dark px-4 text-sm font-semibold text-white whitespace-nowrap cursor-pointer hover:bg-dark transition-colors ${FOCUS} focus-visible:ring-offset-2`}
            >
              <Send size={16} aria-hidden="true" />
              {INVIA}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Il pannello dal basso della barra: i due menù, il totale e «Fatto». */
export function PannelloTutti({ aperto, onChiudi, ...props }: Props & { aperto: boolean; onChiudi: () => void }) {
  const t = totaleCondomini(props.vista.condomini);
  return (
    <PannelloBasso aperto={aperto} onChiudi={onChiudi} titolo={STESSA_OFFERTA} sopra={TUTTI_CONDOMINI}>
      <Menu {...props} t={t} />
      {t.mese !== null && (
        <div className="mt-4 rounded-lg bg-surface p-3">
          <p className="font-heading text-2xl font-semibold leading-none">
            <Importo x={t.mese} />
            <span className="font-sans text-base font-medium text-text"> al mese</span>
          </p>
          <Note t={t} />
        </div>
      )}
      <button
        type="button"
        onClick={onChiudi}
        className={`mt-4 h-12 w-full rounded-lg bg-brand-dark text-base font-semibold text-white cursor-pointer hover:bg-dark transition-colors ${FOCUS} focus-visible:ring-offset-2`}
      >
        {FATTO}
      </button>
    </PannelloBasso>
  );
}

/** Nella copia per l'amministratore (giro 3 del link): lo stesso totale in sola lettura, senza «Stessa offerta per
 *  tutti», a tutte le larghezze. Solo con almeno due condomìni e una cifra: con uno solo direbbe quello che dice già il
 *  suo condominio. */
export function RiquadroTuttiLettura({ condomini, className = "" }: { condomini: Vista["condomini"]; className?: string }) {
  const t = totaleCondomini(condomini);
  if (!totaleInCima(condomini)) return null;
  return (
    // largo quanto il suo contenuto dai 640 px: senza i menù, a tutta larghezza resterebbe mezzo vuoto
    <section aria-label={TUTTI_CONDOMINI} className={`rounded-xl border border-text/40 bg-card p-4 sm:w-fit sm:min-w-80 sm:pr-10 xl:p-5 xl:pr-12 ${className}`}>
      <Totale t={t} />
    </section>
  );
}
